import { useMemo } from 'react';

import { Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { formatPrice } from '../../../shared/utils/format-price.util';
import capitalizeFirstLetter from '../../../shared/utils/capitalize-first-letter';
import { SalesComparison } from '../../types/sales-comparison.type';

const WEEK_DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

const CURRENT_WEEK_COLOR = '#4B70F5';
const LAST_WEEK_COLOR = '#7c7c7c';

interface ChartDataPoint {
  name: string;
  currentWeekFormattedDate: string;
  currentWeekSales: number;
  lastWeekFormattedDate: string;
  lastWeekSales: number;
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: '300px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(3),
    flexGrow: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',

    '& > p': {
      margin: 0,
      fontSize: '12px',
    },
  },
  chart: {
    flexGrow: 3,
  },
  toolTip: {
    padding: theme.spacing(1),
    background: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '5px',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)',

    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),

    '& > p': {
      textTransform: 'capitalize',
      margin: 0,

      '& > span': {
        fontWeight: 'bold',
      },
    },
  },
}));

const formatTick = (tick: number): string => {
  if (tick === 0) return '';
  if (tick >= 1000) return `${(tick / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return tick.toString();
};

function SalesTooltip({ active, payload }: TooltipProps<number, string>) {
  const classes = useStyles();

  if (!active || !payload || payload.length < 2) return null;

  const { currentWeekFormattedDate, lastWeekFormattedDate } = payload[0].payload as ChartDataPoint;

  return (
    <section className={classes.toolTip}>
      <p>
        {currentWeekFormattedDate}:{' '}
        <span style={{ color: CURRENT_WEEK_COLOR }}>{formatPrice(payload[1].value ?? 0)}</span>
      </p>
      <p>
        {lastWeekFormattedDate}: <span style={{ color: LAST_WEEK_COLOR }}>{formatPrice(payload[0].value ?? 0)}</span>
      </p>
    </section>
  );
}

interface Props {
  isLoading: boolean;
  salesComparison: SalesComparison | null;
}

export default function SalesPerformanceChart({ isLoading, salesComparison }: Props) {
  const classes = useStyles();

  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!salesComparison) return [];

    return WEEK_DAYS.map((weekDay) => ({
      name: capitalizeFirstLetter(weekDay),
      currentWeekFormattedDate: salesComparison.currentWeek[weekDay]?.formattedDate ?? '',
      currentWeekSales: salesComparison.currentWeek[weekDay]?.totalPaid ?? 0,
      lastWeekFormattedDate: salesComparison.lastWeek[weekDay]?.formattedDate ?? '',
      lastWeekSales: salesComparison.lastWeek[weekDay]?.totalPaid ?? 0,
    }));
  }, [salesComparison]);

  if (isLoading) return <Skeleton variant="rect" height={300} />;
  if (chartData.length === 0) return null;

  return (
    <Paper className={classes.root}>
      <section className={classes.legend}>
        <p style={{ color: CURRENT_WEEK_COLOR }}>Semaine actuelle</p>
        <p style={{ color: LAST_WEEK_COLOR }}>Semaine dernière</p>
      </section>
      <section className={classes.chart}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 3, left: -25, bottom: -10 }}>
            <defs>
              <linearGradient id="lastWeekGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="10%" stopColor={LAST_WEEK_COLOR} stopOpacity={0.5} />
                <stop offset="100%" stopColor={LAST_WEEK_COLOR} stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="currentWeekGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="10%" stopColor={CURRENT_WEEK_COLOR} stopOpacity={0.5} />
                <stop offset="100%" stopColor={CURRENT_WEEK_COLOR} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#e3e3e3" strokeDasharray="4" />
            <XAxis tickLine={false} axisLine={false} dataKey="name" />
            <YAxis axisLine={false} tickLine={false} tickFormatter={formatTick} />
            <Tooltip content={<SalesTooltip />} cursor={{ strokeWidth: 1, strokeDasharray: 4 }} />
            <Area type="monotone" dataKey="lastWeekSales" stroke={LAST_WEEK_COLOR} fill="url(#lastWeekGradient)" />
            <Area
              type="monotone"
              dataKey="currentWeekSales"
              stroke={CURRENT_WEEK_COLOR}
              fill="url(#currentWeekGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </section>
    </Paper>
  );
}
